import { zodResolver } from '@hookform/resolvers/zod';
import * as Dialog from '@radix-ui/react-dialog';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import {
  Button,
  Card,
  CardContent,
  Input,
  UserSelector,
} from '../../../components/shared';

interface ChargeFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    amount: number,
    comment: string,
    userId?: string
  ) => Promise<boolean>;
  isAdmin: boolean;
  users: Array<{ id: string; name: string }>;
  isSubmitting: boolean;
}

type ChargeFormData = {
  amount: string;
  comment: string;
  userId: string;
};

const ChargeForm: React.FC<ChargeFormProps> = observer(
  ({ isOpen, onClose, onSubmit, isAdmin, users, isSubmitting }) => {
    const { t } = useTranslation();

    // Create schema with translations
    const chargeSchema = React.useMemo(
      () =>
        z.object({
          amount: z
            .string()
            .min(1, t('validation.required'))
            .refine((val) => {
              const num = parseFloat(val);
              return !isNaN(num) && num > 0;
            }, t('billing.forms.charge.amountInvalid')),
          comment: z
            .string()
            .min(1, t('billing.forms.charge.commentRequired'))
            .max(300, t('billing.forms.charge.commentTooLong')),
          userId: isAdmin
            ? z.string().min(1, t('validation.required'))
            : z.string(),
        }),
      [t, isAdmin]
    );

    const form = useForm<ChargeFormData>({
      resolver: zodResolver(chargeSchema),
      defaultValues: {
        amount: '',
        comment: '',
        userId: '',
      },
    });

    const amountInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
      if (isOpen && amountInputRef.current) {
        amountInputRef.current.focus();
      }
    }, [isOpen]);

    const handleSubmit = async (data: ChargeFormData) => {
      const success = await onSubmit(
        parseFloat(data.amount),
        data.comment,
        isAdmin && data.userId ? data.userId : undefined
      );

      if (success) {
        form.reset();
        onClose();
      }
    };

    const handleClose = () => {
      form.reset();
      onClose();
    };

    if (!isOpen) return null;

    const { ref: formAmountFieldRef, ...formAmountField } =
      form.register('amount');

    return (
      <Dialog.Root
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) handleClose();
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-50" />
          <Dialog.Content className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="w-full max-w-md">
              <Card>
                <CardContent className="p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-semibold">
                      {t('billing.actions.charge')}
                    </h2>
                    <button
                      onClick={handleClose}
                      className="text-gray-500 hover:text-gray-700 text-2xl"
                    >
                      ×
                    </button>
                  </div>

                  <form
                    onSubmit={form.handleSubmit(handleSubmit)}
                    className="space-y-4"
                  >
                    {isAdmin && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('billing.forms.user')}{' '}
                          <span className="text-red-500">*</span>
                        </label>
                        <UserSelector
                          users={users}
                          value={form.watch('userId') || ''}
                          onChange={(userId) =>
                            form.setValue('userId', userId, {
                              shouldValidate: true,
                            })
                          }
                          placeholder={t('billing.forms.selectUser')}
                          disabled={isSubmitting}
                        />
                        {form.formState.errors.userId && (
                          <p className="text-red-500 text-sm mt-1">
                            {form.formState.errors.userId.message}
                          </p>
                        )}
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('billing.forms.amount')}{' '}
                        <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="number"
                        step="1"
                        min="1"
                        placeholder="0"
                        {...formAmountField}
                        disabled={isSubmitting}
                        ref={(e) => {
                          amountInputRef.current = e;
                          formAmountFieldRef(e);
                        }}
                      />
                      {form.formState.errors.amount && (
                        <p className="text-red-500 text-sm mt-1">
                          {form.formState.errors.amount.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('billing.forms.charge.comment')}{' '}
                        <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        {...form.register('comment')}
                        placeholder={t(
                          'billing.forms.charge.commentPlaceholder'
                        )}
                        disabled={isSubmitting}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                        rows={3}
                        maxLength={300}
                      />
                      {form.formState.errors.comment && (
                        <p className="text-red-500 text-sm mt-1">
                          {form.formState.errors.comment.message}
                        </p>
                      )}
                    </div>

                    <div className="flex space-x-3 pt-4">
                      <Button
                        type="submit"
                        disabled={isSubmitting || form.formState.isSubmitting}
                        className="flex-1"
                      >
                        {isSubmitting || form.formState.isSubmitting
                          ? t('common.saving')
                          : t('billing.actions.charge')}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleClose}
                        disabled={isSubmitting}
                        className="flex-1"
                      >
                        {t('common.cancel')}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    );
  }
);

export default ChargeForm;
